/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import styles from '../../../../styles/components/pageComponents/program/DuplicateProgramModal.module.css';
import SearchableDropdown from '../../../controlVariations/SearchableDropdown';
import { useSolutions } from '../../../../hooks/useCourse';
import AddSolution from '../../../pageComponents/program/AddSolution'; // adjust path if needed
import ResponsiveModal from '../../../controlVariations/ResponsiveModal';

export interface DuplicateFormData {
	solution: string;
	title: string;
	startDate: string;
	endDate: string;
	copyResources: boolean;
	copyAssessments: boolean;
}

export interface DuplicateProgramModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: DuplicateFormData) => void;
	onAddNewOffering?: () => void;
	initialValues?: Partial<DuplicateFormData>;
}

export const DuplicateProgramModal: React.FC<DuplicateProgramModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	onAddNewOffering,
	initialValues,
}) => {
	const [formData, setFormData] = useState<DuplicateFormData>({
		solution: initialValues?.solution || '',
		title: initialValues?.title || '',
		startDate: initialValues?.startDate || '',
		endDate: initialValues?.endDate || '',
		copyResources: initialValues?.copyResources || false,
		copyAssessments: initialValues?.copyAssessments || false,
	});

	const today = new Date().toISOString().split('T')[0];

	const [errors, setErrors] = useState<
		Partial<Record<keyof DuplicateFormData | 'dateRange', string>>
	>({});

	const {
		solutions,
		createSolution,
		isLoading: solutionsLoading,
	} = useSolutions();

	const [showAddSolutionModal, setShowAddSolutionModal] = useState(false);

	useEffect(() => {
		if (!initialValues || solutions.length === 0) return;

		const matchedSolution = solutions.find(
			sol => sol.title === initialValues.solution
		);

		const today = new Date();
		const nextMonth = new Date();
		nextMonth.setMonth(today.getMonth() + 1);

		setFormData(prev => ({
			...prev,
			solution: matchedSolution?.id || '',
			title: initialValues.title || '',
			startDate:
				initialValues.startDate || today.toISOString().split('T')[0],
			endDate:
				initialValues.endDate || nextMonth.toISOString().split('T')[0],
		}));
	}, [initialValues, solutions]);

	if (!isOpen) return null;

	// const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
	//     const { name, value, type } = e.target;

	//     const today = new Date().toISOString().split("T")[0];

	//     if (type === 'checkbox') {
	//         const checked = (e.target as HTMLInputElement).checked;
	//         setFormData((prev) => ({ ...prev, [name]: checked }));
	//     } else {
	//         // Enforce max length on title change
	//         if (name === 'title' && value.length > 60) return;
	//         setFormData((prev) => ({ ...prev, [name]: value }));
	//     }

	//     // Clear specific error when user starts typing/changing
	//     if (errors[name as keyof DuplicateFormData]) {
	//         setErrors((prev) => ({ ...prev, [name]: undefined, dateRange: undefined }));
	//     }
	// };

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type } = e.target;

		const today = new Date().toISOString().split('T')[0];

		if (type === 'checkbox') {
			const checked = e.target.checked;
			setFormData(prev => ({ ...prev, [name]: checked }));
			return;
		}

		// TITLE
		if (name === 'title') {
			if (value.length > 60) return;
			setFormData(prev => ({ ...prev, title: value }));
			return;
		}

		// 🔥 DATE LOGIC
		if (name === 'startDate') {
			if (value < today) {
				setErrors(prev => ({
					...prev,
					startDate: 'Start date cannot be in the past',
				}));

				setFormData(prev => ({ ...prev, startDate: '' }));
				return;
			}

			// if endDate < new startDate → reset endDate
			if (formData.endDate && value > formData.endDate) {
				setFormData(prev => ({
					...prev,
					startDate: value,
					endDate: '',
				}));

				setErrors(prev => ({
					...prev,
					endDate: 'End date must be after start date',
				}));
				return;
			}

			setFormData(prev => ({ ...prev, startDate: value }));
		}

		if (name === 'endDate') {
			if (value < today) {
				setErrors(prev => ({
					...prev,
					endDate: 'End date cannot be in the past',
				}));

				setFormData(prev => ({ ...prev, endDate: '' }));
				return;
			}

			if (formData.startDate && value < formData.startDate) {
				setErrors(prev => ({
					...prev,
					endDate: 'End date must be after start date',
				}));

				setFormData(prev => ({ ...prev, endDate: '' }));
				return;
			}

			setFormData(prev => ({ ...prev, endDate: value }));
		}

		// clear errors
		setErrors(prev => ({
			...prev,
			[name]: undefined,
			dateRange: undefined,
		}));
	};

	const handleAddSolutionClick = () => {
		setShowAddSolutionModal(true);
	};

	const handleCreateSolution = async (solutionName: string) => {
		try {
			const newSolution = await createSolution({ title: solutionName });

			// auto-select
			setFormData(prev => ({
				...prev,
				solution: newSolution.id,
			}));

			setShowAddSolutionModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	const validate = (): boolean => {
		const newErrors: Partial<
			Record<keyof DuplicateFormData | 'dateRange', string>
		> = {};
		let isValid = true;

		if (!formData.solution) {
			newErrors.solution = 'Offering Category is required.';
			isValid = false;
		}
		if (!formData.title.trim()) {
			newErrors.title = 'Program Title is required.';
			isValid = false;
		}
		// START DATE
		if (!formData.startDate) {
			newErrors.startDate = 'Start date is required.';
			isValid = false;
		} else if (formData.startDate < today) {
			newErrors.startDate = 'Start date cannot be in the past';
			isValid = false;
		}
		// END DATE
		if (!formData.endDate) {
			newErrors.endDate = 'End date is required.';
			isValid = false;
		} else if (formData.endDate < today) {
			newErrors.endDate = 'End date cannot be in the past';
			isValid = false;
		}

		// Date range validation
		if (formData.startDate && formData.endDate) {
			const start = new Date(formData.startDate);
			const end = new Date(formData.endDate);
			if (start > end) {
				newErrors.dateRange =
					'Start date must be before or equal to End date.';
				isValid = false;
			}
		}

		setErrors(newErrors);
		return isValid;
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (validate()) {
			onSubmit(formData);
		}
	};

	return (
		<>
			<div className={styles.overlay} onClick={onClose}>
				<div
					className={styles.modal}
					onClick={e => e.stopPropagation()}
				>
					<div className={styles.header}>
						<h2 className={styles.title}>Duplicate Program</h2>
						<button
							className={styles.closeButton}
							onClick={onClose}
							aria-label="Close"
						>
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<line x1="18" y1="6" x2="6" y2="18"></line>
								<line x1="6" y1="6" x2="18" y2="18"></line>
							</svg>
						</button>
					</div>

					<form onSubmit={handleSubmit}>
						{/* Solution Category */}
						<div className={styles.formGroup}>
							<div className={styles.labelContainer}>
								<label
									className={styles.label}
									htmlFor="category"
								>
									Offering Category{' '}
									<span className={styles.required}>*</span>
								</label>
								<button
									type="button"
									className={styles.addNewBtn}
									onClick={handleAddSolutionClick}
								>
									Add New Offering +
								</button>
							</div>
							<SearchableDropdown
								placeholder="Select Offering"
								options={solutions.map(sol => sol.title)}
								selected={
									solutions.find(
										sol => sol.id === formData.solution
									)?.title
								}
								onSelect={value => {
									const selectedSol = solutions.find(
										sol => sol.title === value
									);
									if (selectedSol) {
										setFormData(prev => ({
											...prev,
											solution: selectedSol.id,
										}));
									}
								}}
							/>
							{errors.solution && (
								<span className={styles.errorText}>
									{errors.solution}
								</span>
							)}
						</div>

						{/* Program Title */}
						<div className={styles.formGroup}>
							<div className={styles.labelContainer}>
								<label className={styles.label} htmlFor="title">
									Program Title{' '}
									<span className={styles.required}>*</span>
								</label>
								<span className={styles.charCount}>
									{formData.title.length}/60
								</span>
							</div>
							<input
								id="title"
								type="text"
								name="title"
								className={`${styles.input} ${styles.titleInput} ${errors.title ? styles.errorInput : ''}`}
								value={formData.title}
								onChange={handleChange}
								placeholder="e.g. IMPACT ICF Level 1 ACC"
							/>
							{errors.title && (
								<span className={styles.errorText}>
									{errors.title}
								</span>
							)}
						</div>

						{/* Date Range */}
						<div className={styles.formGroup}>
							<label className={styles.label}>
								Date <span className={styles.required}>*</span>
							</label>
							<div className={styles.dateContainer}>
								<div className={styles.calendarIcon}>
									<svg
										width="20"
										height="20"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<rect
											x="3"
											y="4"
											width="18"
											height="18"
											rx="2"
											ry="2"
										></rect>
										<line
											x1="16"
											y1="2"
											x2="16"
											y2="6"
										></line>
										<line
											x1="8"
											y1="2"
											x2="8"
											y2="6"
										></line>
										<line
											x1="3"
											y1="10"
											x2="21"
											y2="10"
										></line>
									</svg>
								</div>
								<div className={styles.dateField}>
									{!formData.startDate && (
										<span
											className={styles.datePlaceholder}
										>
											Start Date
										</span>
									)}
									<input
										type="date"
										name="startDate"
										min={today}
										required
										className={`${styles.dateInput} ${errors.startDate || errors.dateRange ? styles.errorInput : ''}`}
										value={formData.startDate}
										onChange={handleChange}
									/>
								</div>
								<span className={styles.dateSeparator}>to</span>
								<div className={styles.dateField}>
									{!formData.endDate && (
										<span
											className={styles.datePlaceholder}
										>
											End Date
										</span>
									)}
									<input
										type="date"
										name="endDate"
										min={formData.startDate || today}
										required
										className={`${styles.dateInput} ${errors.endDate || errors.dateRange ? styles.errorInput : ''}`}
										value={formData.endDate}
										onChange={handleChange}
									/>
								</div>
							</div>
							{(errors.startDate ||
								errors.endDate ||
								errors.dateRange) && (
								<span className={styles.errorText}>
									{errors.startDate ||
										errors.endDate ||
										errors.dateRange}
								</span>
							)}
						</div>

						{/* Checkboxes */}
						<div className={styles.checkboxGroup}>
							<label className={styles.checkboxLabel}>
								<input
									type="checkbox"
									name="copyResources"
									className={styles.checkbox}
									checked={formData.copyResources}
									onChange={handleChange}
								/>
								Include Resource Files
							</label>
							<label className={styles.checkboxLabel}>
								<input
									type="checkbox"
									name="copyAssessments"
									className={styles.checkbox}
									checked={formData.copyAssessments}
									onChange={handleChange}
								/>
								Include Assignments
							</label>
						</div>

						{/* Actions */}
						<div>
							<button type="submit" className={styles.submitBtn}>
								Duplicate Program
							</button>
						</div>
					</form>
				</div>
			</div>

			<ResponsiveModal
				open={showAddSolutionModal}
				onClose={() => setShowAddSolutionModal(false)}
			>
				<AddSolution
					onOk={handleCreateSolution}
					onCancel={() => setShowAddSolutionModal(false)}
				/>
			</ResponsiveModal>
		</>
	);
};
